import React from "react";
import {
  FaAlignLeft,
  FaBullseye,
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaHashtag,
  FaLink,
  FaMapMarkerAlt,
  FaShareAlt,
  FaTags,
  FaUsers,
} from "react-icons/fa";
import { Badge } from "./common";
import { getSocialPlatformConfig } from "../../../components/SocialPlatformIcon";
import { socialPlatformLabel } from "../../../constants/socialPlatforms";

const show = (v) => (v == null || v === "" ? <span className="dmp-muted">—</span> : v);

/** Client's mail / social accounts as cards. */
export function SocialAccountsList({ accounts = [] }) {
  if (accounts.length === 0) {
    return (
      <div className="dmp-empty">No accounts added yet (Gmail, GMB, Facebook, Instagram, LinkedIn, YouTube, WA Business).</div>
    );
  }
  return (
    <div className="dmp-accounts">
      {accounts.map((a) => {
        const config = getSocialPlatformConfig(a.Platform_Name);
        const PlatformIcon = config?.Icon || FaShareAlt;
        const login = a.Email_Used || a.Username;
        const profileUrl = a.Profile_URL || a.Business_URL;
        return (
          <div key={a.Account_Id} className="dmp-account">
            <span
              className="dmp-account__icon"
              style={{ background: config?.color || "#64748b" }}
              aria-hidden="true"
            >
              <PlatformIcon />
            </span>
            <div className="dmp-account__body">
              <div className="dmp-account__top">
                <strong className="dmp-account__name">{a.Account_Name || socialPlatformLabel(a.Platform_Name)}</strong>
                <Badge value={a.Status} />
              </div>
              <div className="dmp-account__platform">{socialPlatformLabel(a.Platform_Name) || a.Platform_Name}</div>
              {login ? <div className="dmp-account__login">{login}</div> : null}
            </div>
            {profileUrl ? (
              <a
                className="dmp-account__open"
                href={profileUrl}
                target="_blank"
                rel="noreferrer"
                title="Open profile"
                aria-label="Open profile"
              >
                <FaExternalLinkAlt aria-hidden="true" />
              </a>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function parseDay(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

const prettyDay = (date) =>
  date ? date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.slice(0, 2).map((p) => p[0].toUpperCase()).join("") : "?";
}

function BriefField({ icon: Icon, label, children }) {
  return (
    <div className="dmp-brief__field">
      <span className="dmp-brief__icon" aria-hidden="true">
        <Icon />
      </span>
      <div className="dmp-brief__field-body">
        <span className="dmp-brief__label">{label}</span>
        <div className="dmp-brief__value">{children}</div>
      </div>
    </div>
  );
}

function BriefChips({ items, icon: Icon }) {
  if (!items.length) return <span className="dmp-muted">—</span>;
  return (
    <div className="dmp-brief__chips">
      {items.map((item) => (
        <span key={item} className="dmp-brief__chip">
          <Icon aria-hidden="true" />
          {item}
        </span>
      ))}
    </div>
  );
}

/** Project brief: linked project, schedule, team, keywords, target areas and description. */
export function ProjectBriefDetails({ project }) {
  const start = parseDay(project?.Start_Date);
  const end = parseDay(project?.End_Date);
  const days = start && end && end >= start ? Math.round((end - start) / 86400000) + 1 : null;
  const team = [
    { role: "Assigned", name: project?.Assigned_To_Name },
    { role: "Prepared", name: project?.Prepared_By_Name },
    { role: "Reviewed", name: project?.Reviewed_By_Name },
  ];
  const keywords = (Array.isArray(project?.Keywords) ? project.Keywords : []).filter(Boolean);
  const targets = (Array.isArray(project?.Target_Locations) ? project.Target_Locations : [])
    .map((l) => (l?.radius_km != null ? `${l.name} · ${l.radius_km} km` : l?.name))
    .filter(Boolean);

  return (
    <div className="dmp-brief">
      <div className="dmp-brief__grid">
        <BriefField icon={FaLink} label="Linked client project">
          {show(project?.Tenant_Project_Name)}
        </BriefField>
        <BriefField icon={FaMapMarkerAlt} label="Location">
          {show(project?.Location)}
        </BriefField>
        <BriefField icon={FaCalendarAlt} label="Schedule">
          {start || end ? (
            <span className="dmp-brief__dates">
              {prettyDay(start)} <span className="dmp-brief__arrow">→</span> {prettyDay(end)}
              {days ? <span className="dmp-brief__days">{days} {days === 1 ? "day" : "days"}</span> : null}
            </span>
          ) : (
            show(null)
          )}
        </BriefField>
      </div>

      <div className="dmp-brief__block">
        <span className="dmp-brief__block-title">
          <FaUsers aria-hidden="true" /> Team
        </span>
        <div className="dmp-brief__team">
          {team.map((member) => (
            <div key={member.role} className="dmp-brief__person">
              <span className={`dmp-brief__avatar${member.name ? "" : " is-empty"}`} aria-hidden="true">
                {member.name ? initials(member.name) : "—"}
              </span>
              <span className="dmp-brief__person-text">
                <span className="dmp-brief__person-role">{member.role}</span>
                <span className="dmp-brief__person-name">{member.name || "Not set"}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="dmp-brief__block">
        <span className="dmp-brief__block-title">
          <FaTags aria-hidden="true" /> Keywords
        </span>
        <BriefChips items={keywords} icon={FaHashtag} />
      </div>

      <div className="dmp-brief__block">
        <span className="dmp-brief__block-title">
          <FaBullseye aria-hidden="true" /> Target locations
        </span>
        <BriefChips items={targets} icon={FaMapMarkerAlt} />
      </div>

      <div className="dmp-brief__block">
        <span className="dmp-brief__block-title">
          <FaAlignLeft aria-hidden="true" /> Description
        </span>
        {project?.Description ? (
          <p className="dmp-brief__desc">{project.Description}</p>
        ) : (
          <span className="dmp-muted">—</span>
        )}
      </div>
    </div>
  );
}
